import { FlaggedFeature } from 'hooks/useFlipperFlag'
import { makeAutoObservable, observable } from 'mobx'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UserClient } from 'api/clients/UserClient'
import { checkFeatureFlagUserGroup, USERS } from 'utils/featureFlagUserGroups'
import { Auth } from 'types'
import { authCookie } from 'utils/cookies'
import qs from 'qs'

const LOCAL_STORAGE_KEY = 'FLIPPER_OVERRIDES'
const UNAUTH_FLAGS: FlaggedFeature[] = ['dfy_login_cta']

const FeatureQueryParamOverrideWhitelist = ['brand_plan_strategies', 'dfy_login_cta']

export class FeatureFlagsStore {
  private _overrides = observable.map<FlaggedFeature, boolean>()
  _flags = observable.map<FlaggedFeature, boolean>()
  isLoading = false
  private _allowOverrides = false
  private _auth: Auth | null = authCookie || null
  ready = false

  constructor() {
    makeAutoObservable(this)
    this._allowOverrides = checkFeatureFlagUserGroup([USERS.withAlmanacEmail], this._auth?.email)
    this.hydrate()
  }

  clearFlags = () => {
    this._flags.clear()
  }

  setAuth = (auth: Auth | null) => {
    this._auth = auth
    this._allowOverrides = checkFeatureFlagUserGroup([USERS.withAlmanacEmail], this._auth?.email)
    this.clear()
    this._allowOverrides && this.hydrate()
  }

  overrideFlag = (flag: FlaggedFeature, enabled: boolean) => {
    if (!this._allowOverrides) {
      return
    }

    this._overrides.set(flag, enabled)
    this._synchronise()
  }

  setFlag = (flag: FlaggedFeature, enabled: boolean) => {
    this._flags.set(flag, enabled)
    this._synchronise()
  }

  isActive = (flag: FlaggedFeature) => {
    if (this._allowOverrides && this._overrides.has(flag)) {
      return this._overrides.get(flag) || false
    }

    return this._flags.get(flag) || false
  }

  isOverridden = (flag: FlaggedFeature) => {
    if (!this._allowOverrides) {
      return false
    }

    return this._overrides.has(flag) && this._overrides.get(flag) !== (this._flags.get(flag) || false)
  }

  hydrate = () => {
    const flags = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (!flags) {
      return
    }

    const parsedFlags = JSON.parse(flags)
    for (const flag in parsedFlags) {
      this.overrideFlag(flag as FlaggedFeature, parsedFlags[flag])
    }
  }

  setLoading = (loading: boolean) => {
    this.isLoading = loading
  }

  setReady = (ready: boolean) => {
    this.ready = ready
  }

  load = async () => {
    this.setLoading(true)
    // eslint-disable-next-line no-restricted-properties
    const { features } = qs.parse(window.location.search, { ignoreQueryPrefix: true })
    const featureFlags = Array.isArray(features) ? features : [features].filter(Boolean)
    const allowedFlags = featureFlags.filter((feature) => FeatureQueryParamOverrideWhitelist.includes(feature))

    const response = await UserClient.getFeatures()
    if (response && response.type === SUCCESS_RESPONSE_TYPE && response.data && Array.isArray(response.data)) {
      response.data.forEach((flag) => {
        this.setFlag(flag, true)
      })
    }

    allowedFlags.forEach((feature) => {
      this.setFlag(feature as FlaggedFeature, true)
    })

    this.setLoading(false)
    this.setReady(true)
  }

  reset = () => {
    this._overrides.clear()
    this._flags.clear()
    this.clearStorage()
    this.setReady(false)
    return this.load()
  }

  resetFlag = (flag: FlaggedFeature) => {
    this._overrides.delete(flag)
  }

  flags = () => {
    if (this._allowOverrides) {
      return observable.map([...this._flags, ...this._overrides])
    }

    return this._flags
  }

  clear = () => {
    const preservedFlags = new Map<FlaggedFeature, boolean>()

    this._overrides.forEach((enabled, flag) => {
      // on logout only preserve _override flags if its an _unauthflag, enabled, and not in _flags
      if (!this._flags.has(flag) && UNAUTH_FLAGS.includes(flag) && enabled) {
        preservedFlags.set(flag, enabled)
      }
    })

    this._overrides.clear()
    this._flags.clear()

    preservedFlags.forEach((enabled, flag) => {
      this._overrides.set(flag, enabled)
    })

    this.setReady(false)
    this._auth = null
  }

  private _synchronise = () => {
    this._allowOverrides && localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(Object.fromEntries(this._overrides)))
  }

  private clearStorage = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY)
  }
}
